(() => {
  if (window.mtoSwatchPickerLoaded) return;
  window.mtoSwatchPickerLoaded = true;

  let activeDrawer = null;
  let lastFocusedElement = null;

  const SWATCH_INFO_ICON =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9.25" stroke="currentColor" stroke-width="1.75"/><path d="M12 11.25v5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><circle cx="12" cy="8" r="1.15" fill="currentColor"/></svg>';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const normalizeCategories = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item && typeof item === 'object') {
            return String(item.label || item.title || item.name || item.value || '').trim();
          }
          return String(item || '').trim();
        })
        .filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  };

  const CATEGORY_COLORS = {
    beige: '#D9C4A8',
    cream: '#F3E6C8',
    white: '#F7F7F7',
    ivory: '#FFFFF0',
    gray: '#8A8F98',
    grey: '#8A8F98',
    charcoal: '#4B4F56',
    black: '#1A1A1A',
    brown: '#6B3E26',
    blue: '#2F5EA8',
    navy: '#1E3A6E',
    green: '#3F7A4A',
    tan: '#C4A574',
    orange: '#E07A2F',
    red: '#C0392B',
    yellow: '#E2C44A',
    purple: '#6B4C9A',
    pink: '#D47A9A',
  };

  const PILLOW_CATEGORY_COLORS = {
    beige: '#AE9F84',
    blue: '#0000FF',
    brown: '#8B4513',
    cream: '#F5F5DC',
    gray: '#808080',
    grey: '#808080',
    green: '#008000',
    orange: '#FF8C00',
    white: '#FFFFFF',
    black: '#000000',
    red: '#FF0000',
    tan: '#ECAB32',
  };

  const PILLOW_DISPLAY_NAMES = {
    beige: 'Beige',
    blue: 'Blue',
    brown: 'Brown',
    cream: 'Cream',
    gray: 'Gray',
    grey: 'Gray',
    green: 'Green',
    orange: 'Orange',
    white: 'White',
    black: 'Black',
    red: 'Red',
    tan: 'Tan',
  };

  const PILLOW_HEX_TO_NAME = {
    '#ae9f84': 'Beige',
    '#0000ff': 'Blue',
    '#8b4513': 'Brown',
    '#f5f5dc': 'Cream',
    '#808080': 'Gray',
    '#008000': 'Green',
    '#ff8c00': 'Orange',
    '#ffffff': 'White',
    '#000000': 'Black',
    '#ff0000': 'Red',
    '#ecab32': 'Tan',
    '#d2b48c': 'Tan',
    '#fffdd0': 'Cream',
    '#ffa500': 'Orange',
    '#d9c4a8': 'Beige',
  };

  const canonicalCategoryColor = (name) => CATEGORY_COLORS[normalizeToken(name)] || '';

  const preserveScrollPosition = (callback) => {
    const x = window.scrollX;
    const y = window.scrollY;
    const restore = () => {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo(x, y);
      }
    };

    callback();
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  };

  const syncLabelState = (input, scope) => {
    if (!input) return;

    const groupName = input.getAttribute('name');
    if (!groupName) return;

    const root = scope instanceof Element ? scope : input.closest('[data-vpl2-picker]') || document;

    root.querySelectorAll(`input[name="${CSS.escape(groupName)}"]`).forEach((radio) => {
      const label = root.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      label?.classList.toggle('is-active', radio.checked);
    });
  };

  const getMtoPicker = (target) => target?.closest?.('[data-vpl2-mto-picker]') || null;

  const getCustomSwatchRoot = (target) => {
    const root = target.closest('[data-vpl2-custom-swatch]');
    if (!root || !getMtoPicker(root)) return null;
    return root;
  };

  const isPillowGroup = (root) => {
    if (root?.hasAttribute('data-vpl2-pillow-group')) return true;
    const section = root?.getAttribute('data-vpl2-section') || '';
    return /pillow/i.test(section);
  };

  const MAX_MULTIPLE_PILLOWS = 2;

  const allowsMultiplePillows = (root) =>
    Boolean(isPillowGroup(root) && root?.hasAttribute('data-vpl2-allow-multiple'));

  const getMaxMultiplePillows = (root) => {
    const raw = Number(root?.getAttribute('data-vpl2-max-multiple'));
    return Number.isFinite(raw) && raw > 0 ? raw : MAX_MULTIPLE_PILLOWS;
  };

  const getCheckedSwatchInputs = (root) =>
    [...(root?.querySelectorAll('[data-vpl2-swatch-input]:checked') || [])];

  const enforceMultiplePillowLimit = (root) => {
    if (!allowsMultiplePillows(root)) return;

    const max = getMaxMultiplePillows(root);
    const checked = getCheckedSwatchInputs(root);

    if (checked.length > max) {
      checked.slice(max).forEach((input) => {
        input.checked = false;
        document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
      });
    }

    const atLimit = getCheckedSwatchInputs(root).length >= max;

    root.querySelectorAll('[data-vpl2-swatch-input]').forEach((input) => {
      const shouldDisable = atLimit && !input.checked;
      input.disabled = shouldDisable;
      input.classList.toggle('disabled', shouldDisable);
      const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      label?.classList.toggle('is-disabled', shouldDisable);
      label?.classList.toggle('disabled', shouldDisable);
    });
  };

  const isLeatherMaterialValue = (value) => normalizeMatchToken(value).includes('leather');

  const isFabricMaterialValue = (value) => {
    const token = normalizeMatchToken(value);
    return token === 'fabric' || token.includes('fabric');
  };

  const shouldShowPillowForMaterial = (material) => {
    if (!material) return true;
    if (isLeatherMaterialValue(material)) return false;
    return isFabricMaterialValue(material);
  };

  const normalizeHex = (value) => {
    const hex = String(value || '').trim();
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase();
    if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex.toLowerCase()}`;
    return '';
  };

  const resolvePillowCategory = (raw) => {
    const text = String(raw || '').trim();
    if (!text) return null;

    const hexMatch = text.match(/#?[0-9a-fA-F]{6}/);
    const hexFromText = hexMatch ? normalizeHex(hexMatch[0]) : '';
    const nameFromText = text
      .replace(/#?[0-9a-fA-F]{6}/g, '')
      .replace(/[-–—:,|/]+/g, ' ')
      .trim();
    const tokenFromName = normalizeToken(nameFromText);
    const nameFromHex = hexFromText ? PILLOW_HEX_TO_NAME[hexFromText] : '';
    const displayName =
      (tokenFromName && PILLOW_DISPLAY_NAMES[tokenFromName]) ||
      nameFromHex ||
      nameFromText;

    if (!displayName) return null;

    const token = normalizeToken(displayName);
    const hex = PILLOW_CATEGORY_COLORS[token] || hexFromText || '';
    return {
      name: PILLOW_DISPLAY_NAMES[token] || displayName,
      hex,
      token,
    };
  };

  const shouldFilterByProductOptions = (root) =>
    Boolean(root?.hasAttribute('data-vpl2-filter-by-product-options'));

  const getOptionDependentSwatchRoots = (picker) =>
    [...(picker?.querySelectorAll('[data-vpl2-custom-swatch][data-vpl2-filter-by-product-options]') || [])];

  const getDrawerForRoot = (root) => {
    const sectionId = root?.getAttribute('data-vpl2-section');
    if (!sectionId) return null;
    return document.getElementById(`Vpl2MaterialDrawer-${sectionId}`);
  };

  const normalizeToken = (value) => String(value || '').trim().toLowerCase();

  const splitTokens = (value) =>
    String(value || '')
      .split('||')
      .map((token) => token.trim())
      .filter(Boolean);

  const isMaterialOptionName = (name) => {
    const token = normalizeToken(name);
    return token === 'material' || token === 'materials';
  };

  const isGradeOptionName = (name) => normalizeToken(name) === 'grade';

  const isAvailabilityOptionName = (name) => normalizeToken(name) === 'availability';

  const isVisibleOption = (input) => Boolean(input) && !input.closest('[hidden]');

  const getSelectedOptionValue = (root, isOptionName) => {
    const picker = root?.closest('[data-vpl2-picker]');
    if (!picker) return '';

    const optionInputs = [...picker.querySelectorAll('input[data-variant-input]')].filter((input) =>
      isOptionName(input.getAttribute('name'))
    );

    if (!optionInputs.length) return '';

    const checked = optionInputs.find((input) => input.checked);
    return (checked?.value || '').trim();
  };

  const getSelectedMaterial = (root) => getSelectedOptionValue(root, isMaterialOptionName);

  const getSelectedGrade = (root) => getSelectedOptionValue(root, isGradeOptionName);

  const getSelectedAvailability = (root) => getSelectedOptionValue(root, isAvailabilityOptionName);

  // Normalize "In Stock" / "Instock" / "Made-to-Order" for reliable matching.
  const normalizeMatchToken = (value) => normalizeToken(value).replace(/[\s_-]+/g, '');

  const normalizeTitleToken = (value) => normalizeMatchToken(value);

  const getProductInStockColorNames = (root) => {
    const dataNode = root?.querySelector('[data-vpl2-in-stock-colors]');
    if (!dataNode) return [];

    try {
      const parsed = JSON.parse(dataNode.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map((name) => String(name || '').trim()).filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const matchesInStockTitle = (itemTitle, inStockNames) => {
    if (!inStockNames.length) return false;

    const titleToken = normalizeTitleToken(itemTitle);
    if (!titleToken) return false;

    return inStockNames.some((name) => normalizeTitleToken(name) === titleToken);
  };

  const isInStockAvailabilityValue = (value) => normalizeMatchToken(value) === 'instock';

  const updateAvailabilityShippingMessage = (picker) => {
    const message = picker?.querySelector('[data-vpl2-shipping-message]');
    if (!message) return;

    const selectedInput = [...picker.querySelectorAll('input[data-variant-input]:checked')].find((input) =>
      isAvailabilityOptionName(input.getAttribute('name'))
    );

    message.textContent = selectedInput?.getAttribute('data-vpl2-shipping-message-value') || '';
  };

  // Material/Grade/Availability drive the Custom Swatch list, so keep them open together.
  const isCustomSwatchTriggerRow = (row) => {
    const optionName = row?.querySelector('input[data-variant-input]')?.getAttribute('name');
    return isMaterialOptionName(optionName) || isGradeOptionName(optionName) || isAvailabilityOptionName(optionName);
  };

  const openRow = (row) => {
    if (!row || row.hasAttribute('hidden') || row.classList.contains('vpl2-hidden-option')) return;
    row.classList.add('is-open');
    row.querySelector('[data-vpl2-toggle]')?.setAttribute('aria-expanded', 'true');
  };

  const closeRow = (row) => {
    if (!row) return;
    row.classList.remove('is-open');
    row.querySelector('[data-vpl2-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const openCustomSwatchRows = (picker) => {
    getOptionDependentSwatchRoots(picker).forEach(openRow);
  };

  const openMaterialRows = (picker) => {
    picker?.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      const optionName = row.querySelector('input[data-variant-input]')?.getAttribute('name');
      if (isMaterialOptionName(optionName)) openRow(row);
    });
  };

  const splitFieldValues = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }

    return String(value || '')
      .split(/\|\||,/)
      .map((token) => token.trim())
      .filter(Boolean);
  };

  const matchesOptionValue = (itemValue, selectedValue) => {
    // No option / no selection => keep previous "show all" behavior.
    if (!selectedValue) return true;
    // When an option is selected, only exact matches are shown.
    if (!itemValue) return false;
    return normalizeMatchToken(itemValue) === normalizeMatchToken(selectedValue);
  };

  const matchesMaterial = (itemMaterial, selectedMaterial) => {
    if (!selectedMaterial) return true;
    const values = splitFieldValues(itemMaterial);
    if (!values.length) return false;
    return values.some((value) => matchesOptionValue(value, selectedMaterial));
  };

  const normalizeGradeToken = (value) => {
    const token = normalizeMatchToken(value);
    const withoutPrefix = token.replace(/^grade/, '');
    return withoutPrefix || token;
  };

  const matchesGrade = (itemGrade, selectedGrade) => {
    if (!selectedGrade) return true;
    const values = splitFieldValues(itemGrade);
    if (!values.length) return false;
    const selectedToken = normalizeGradeToken(selectedGrade);
    return values.some((value) => normalizeGradeToken(value) === selectedToken);
  };

  const matchesAvailability = (itemAvailability, selectedAvailability) =>
    matchesOptionValue(itemAvailability, selectedAvailability);

  const normalizeAvailabilityValues = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  };

  const getSelectedCustomSwatchValues = (root) => {
    if (!root || root.hasAttribute('hidden')) {
      return { category: '', swatch: '', swatches: [] };
    }

    const multiple = allowsMultiplePillows(root);
    const maxMultiple = multiple ? getMaxMultiplePillows(root) : 0;
    const checkedSwatches = [...(root.querySelectorAll('[data-vpl2-swatch-input]:checked') || [])]
      .filter((input) => (multiple ? Boolean(input.value?.trim()) : isVisibleOption(input)))
      .slice(0, multiple ? maxMultiple : undefined);
    const swatches = checkedSwatches.map((input) => String(input.value || '').trim()).filter(Boolean);

    let category = '';
    if (multiple) {
      const categories = [];
      checkedSwatches.forEach((input) => {
        const item = input.closest('[data-vpl2-swatch-item]');
        splitTokens(item?.getAttribute('data-categories') || '').forEach((name) => {
          if (name && !categories.some((existing) => normalizeToken(existing) === normalizeToken(name))) {
            categories.push(name);
          }
        });
      });
      category = categories.join(', ');
    } else {
      category =
        [...(root.querySelectorAll('[data-vpl2-category-input]:checked') || [])]
          .find(isVisibleOption)
          ?.value?.trim() || '';
    }

    return {
      category,
      swatch: multiple ? swatches.join(', ') : swatches[0] || '',
      swatches,
    };
  };

  const updateCustomSwatchSummary = (root) => {
    if (!root) return;

    const { category, swatch } = getSelectedCustomSwatchValues(root);
    const categoryDisplay = root.querySelector('[data-vpl2-category-display]');
    const swatchDisplays = root.querySelectorAll('[data-vpl2-swatch-display]');
    const categoryProperty = root.querySelector('[data-vpl2-category-property]');
    const swatchProperty = root.querySelector('[data-vpl2-swatch-property]');

    if (categoryDisplay) categoryDisplay.textContent = category;
    swatchDisplays.forEach((el) => {
      el.textContent = swatch;
    });
    if (categoryProperty) categoryProperty.value = category;
    if (swatchProperty) swatchProperty.value = swatch;
  };

  const getProductFormId = (form) => {
    if (!(form instanceof HTMLFormElement)) return '';
    // Shopify product forms include <input name="id">, which shadows form.id.
    return form.getAttribute('id') || '';
  };

  const getCustomSwatchRootsForForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return [];

    const formId = getProductFormId(form);
    if (formId) {
      const matched = [
        ...document.querySelectorAll(
          `[data-vpl2-mto-picker] [data-vpl2-custom-swatch][data-vpl2-form="${CSS.escape(formId)}"]`
        ),
      ];
      if (matched.length) return matched;
    }

    const isProductForm =
      form.classList.contains('product-single__form') ||
      (form.getAttribute('action') || '').includes('/cart/add');
    if (!isProductForm) return [];

    return [...document.querySelectorAll('[data-vpl2-mto-picker] [data-vpl2-custom-swatch]')];
  };

  const getSwatchGroupTitle = (root) =>
    root?.querySelector('.vpl2-title')?.textContent?.trim() || 'option';

  const hasSelectableSwatches = (root) =>
    [...(root?.querySelectorAll('[data-vpl2-swatch-item]') || [])].some((item) => {
      if (item.hasAttribute('hidden')) return false;
      const input = item.querySelector('[data-vpl2-swatch-input]');
      return Boolean(input && !input.disabled && !input.classList.contains('disabled'));
    });

  const getMissingRequiredSwatchGroups = (roots) =>
    (roots || []).filter((root) => {
      if (!root || root.hasAttribute('hidden')) return false;
      if (!hasSelectableSwatches(root)) return false;
      return !getSelectedCustomSwatchValues(root).swatch;
    });

  const clearRequiredSwatchError = (root) => {
    root?.classList.remove('is-required-error');
    root?.removeAttribute('data-mto-required-error');
  };

  const markRequiredSwatchError = (root) => {
    if (!root) return;
    root.classList.add('is-required-error');
    root.setAttribute('data-mto-required-error', '');
    openRow(root);
  };

  const getRequiredDialog = (picker) => picker?.querySelector('[data-mto-required-dialog]') || null;

  const closeRequiredOptionsDialog = (picker) => {
    const dialog = getRequiredDialog(picker);
    if (!dialog) return;

    dialog.hidden = true;
    dialog.classList.remove('is-open');
    document.body.classList.remove('mto-required-dialog-open');

    const firstMissing = picker.querySelector('[data-mto-required-error]');
    firstMissing?.querySelector('[data-vpl2-toggle]')?.focus({ preventScroll: false });
    firstMissing?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const showRequiredOptionsDialog = (picker, missingRoots) => {
    if (!picker || !missingRoots?.length) return;

    const dialog = getRequiredDialog(picker);
    const heading = dialog?.querySelector('[data-mto-required-heading]');
    const text = dialog?.querySelector('[data-mto-required-text]');
    const list = dialog?.querySelector('[data-mto-required-list]');
    const titles = missingRoots.map(getSwatchGroupTitle).filter(Boolean);
    const optionLabel = titles.join(' and ');
    const template =
      picker.getAttribute('data-mto-required-message') ||
      'Please select {{ option }} before adding this item to your cart.';

    picker.querySelectorAll('[data-vpl2-custom-swatch]').forEach(clearRequiredSwatchError);
    missingRoots.forEach(markRequiredSwatchError);

    if (heading) {
      heading.textContent = picker.getAttribute('data-mto-required-title') || 'Select required options';
    }
    if (text) {
      text.textContent = template.replace(/\{\{\s*option\s*\}\}/g, optionLabel);
    }
    if (list) {
      list.hidden = titles.length < 2;
      list.innerHTML = titles.length > 1 ? titles.map((title) => `<li>${escapeHtml(title)}</li>`).join('') : '';
    }

    if (!dialog) {
      window.alert(template.replace(/\{\{\s*option\s*\}\}/g, optionLabel));
      missingRoots[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    dialog.hidden = false;
    requestAnimationFrame(() => {
      dialog.classList.add('is-open');
      document.body.classList.add('mto-required-dialog-open');
      dialog.querySelector('[data-mto-required-panel]')?.focus({ preventScroll: true });
    });
  };

  const validateRequiredMtoSwatches = (form) => {
    const roots = getCustomSwatchRootsForForm(form);
    if (!roots.length) return true;

    roots.forEach((root) => {
      enforceMultiplePillowLimit(root);
      updateCustomSwatchSummary(root);
    });

    const missing = getMissingRequiredSwatchGroups(roots);
    if (!missing.length) {
      roots.forEach(clearRequiredSwatchError);
      return true;
    }

    const picker = getMtoPicker(missing[0]) || getMtoPicker(roots[0]);
    showRequiredOptionsDialog(picker, missing);
    return false;
  };

  const syncCustomSwatchPropertiesToFormData = (form, formData) => {
    if (!(form instanceof HTMLFormElement) || !formData) return;

    const roots = getCustomSwatchRootsForForm(form);
    if (!roots.length) return;

    roots.forEach((root) => {
      updateCustomSwatchSummary(root);

      const { category, swatch } = getSelectedCustomSwatchValues(root);
      const swatchProperty = root.querySelector('[data-vpl2-swatch-property]');
      const categoryProperty = root.querySelector('[data-vpl2-category-property]');

      const swatchPropName = swatchProperty?.getAttribute('name') || 'properties[Custom Swatch]';
      const categoryPropName = categoryProperty?.getAttribute('name') || 'properties[Color Category]';

      if (swatch) formData.set(swatchPropName, swatch);
      else formData.delete(swatchPropName);

      if (category && categoryPropName) formData.set(categoryPropName, category);
      else if (categoryPropName) formData.delete(categoryPropName);
    });
  };

  const filterCustomSwatches = (root) => {
    if (!root) return;

    const filterByProductOptions = shouldFilterByProductOptions(root);
    const selectedMaterial = filterByProductOptions ? getSelectedMaterial(root) : '';
    const selectedGrade = filterByProductOptions ? getSelectedGrade(root) : '';
    const selectedAvailability = filterByProductOptions ? getSelectedAvailability(root) : '';
    const productInStockNames = getProductInStockColorNames(root);
    const restrictToProductInStockColors =
      isInStockAvailabilityValue(selectedAvailability) &&
      (root.hasAttribute('data-vpl2-has-in-stock-colors') || productInStockNames.length > 0);
    const categoryItems = [...root.querySelectorAll('[data-vpl2-category-item]')];
    const items = [...root.querySelectorAll('[data-vpl2-swatch-item]')];

    const itemMatchesBaseFilters = (item) => {
      const itemMaterial = item.getAttribute('data-material') || '';
      const itemGrade = item.getAttribute('data-grade') || '';
      const itemAvailabilities = splitTokens(item.getAttribute('data-availabilities'));
      const itemTitle = item.getAttribute('data-title') || '';
      const matchesSelectedMaterial = matchesMaterial(itemMaterial, selectedMaterial);
      const matchesSelectedGrade = matchesGrade(itemGrade, selectedGrade);
      const matchesProductInStockColor =
        item.getAttribute('data-in-stock-on-product') === 'true' ||
        matchesInStockTitle(itemTitle, productInStockNames);

      if (restrictToProductInStockColors) {
        return matchesSelectedMaterial && matchesSelectedGrade && matchesProductInStockColor;
      }

      const matchesSelectedAvailability =
        !selectedAvailability ||
        itemAvailabilities.some((availability) =>
          matchesAvailability(availability, selectedAvailability)
        );

      return matchesSelectedMaterial && matchesSelectedGrade && matchesSelectedAvailability;
    };

    const visibleCategoryNames = new Set();
    items.forEach((item) => {
      if (!itemMatchesBaseFilters(item)) return;
      splitTokens(item.getAttribute('data-categories')).forEach((categoryName) => {
        visibleCategoryNames.add(categoryName);
      });
    });

    const visibleCategoryTokens = new Set(
      [...visibleCategoryNames].map((name) => normalizeToken(name))
    );

    categoryItems.forEach((item) => {
      const categoryName =
        item.getAttribute('data-category') ||
        item.querySelector('[data-vpl2-category-input]')?.value ||
        '';
      const circle = item.querySelector('.vpl2-swatch-circle');
      const hasCategoryColor = Boolean(
        item.getAttribute('data-vpl2-color') ||
          circle?.getAttribute('data-vpl2-color') ||
          circle?.style.backgroundColor
      );
      // Only show categories that have a color and at least one matching swatch
      // for the current Material/Grade (first metaobject) or all swatches (later groups).
      const matches =
        hasCategoryColor && visibleCategoryTokens.has(normalizeToken(categoryName));

      item.hidden = !matches;

      const input = item.querySelector('[data-vpl2-category-input]');
      if (!matches && input) {
        input.checked = false;
        document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
      }
    });

    const visibleCategoryInputs = categoryItems
      .filter((item) => !item.hidden)
      .map((item) => item.querySelector('[data-vpl2-category-input]'))
      .filter(Boolean);

    let selectedCategory = visibleCategoryInputs.find((input) => input.checked) || null;

    if (!selectedCategory && visibleCategoryInputs.length) {
      selectedCategory = visibleCategoryInputs[0];
      selectedCategory.checked = true;
      syncLabelState(selectedCategory);
    }

    const category = selectedCategory?.value || '';
    let visibleChecked = null;

    items.forEach((item) => {
      const categories = splitTokens(item.getAttribute('data-categories'));
      const matchesCategory =
        !category ||
        categories.length === 0 ||
        categories.some((itemCategory) => normalizeToken(itemCategory) === normalizeToken(category));
      const isVisible = itemMatchesBaseFilters(item) && matchesCategory;

      item.hidden = !isVisible;

      const input = item.querySelector('[data-vpl2-swatch-input]');
      if (!isVisible && input) {
        const keepHiddenSelection =
          allowsMultiplePillows(root) && itemMatchesBaseFilters(item);
        if (!keepHiddenSelection) {
          input.checked = false;
          document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
        }
      }
      if (isVisible && input?.checked) visibleChecked = input;
    });

    if (!visibleChecked && !allowsMultiplePillows(root) && !root.hasAttribute('hidden')) {
      const firstVisible = items
        .find((item) => !item.hidden)
        ?.querySelector('[data-vpl2-swatch-input]');

      if (firstVisible) {
        firstVisible.checked = true;
        syncLabelState(firstVisible);
      }
    }

    const visibleSwatches = items.filter((item) => !item.hidden);
    const hasVisibleSwatches = visibleSwatches.length > 0;
    const visibleCategories = categoryItems.filter((item) => !item.hidden);
    const hasVisibleCategories = visibleCategories.length > 0;

    const noSwatchesMsg = root.querySelector('[data-vpl2-no-swatches-msg]');
    const categoryFieldset = root.querySelector('.vpl2-category-grid')?.closest('fieldset');
    const divider = root.querySelector('.vpl2-divider');

    if (noSwatchesMsg) {
      noSwatchesMsg.hidden = hasVisibleSwatches;
    }

    if (categoryFieldset) {
      categoryFieldset.hidden = !hasVisibleCategories || !hasVisibleSwatches;
    }

    if (divider) {
      divider.hidden = !hasVisibleCategories || !hasVisibleSwatches;
    }

    enforceMultiplePillowLimit(root);
    updateCustomSwatchSummary(root);
    updateOptionAvailabilityStates(root.closest('[data-vpl2-picker]'));
  };

  const updateOptionAvailabilityStates = (picker) => {
    if (!(picker instanceof Element)) return;

    const roots = getOptionDependentSwatchRoots(picker);
    if (!roots.length) return;

    const primaryRoot = roots[0];
    const swatchItems = [...primaryRoot.querySelectorAll('[data-vpl2-swatch-item]')];
    if (!swatchItems.length) return;

    const selectedMaterial = getSelectedMaterial(primaryRoot);
    const selectedGrade = getSelectedGrade(primaryRoot);
    const selectedAvailability = getSelectedAvailability(primaryRoot);

    const productInStockNames = getProductInStockColorNames(primaryRoot);
    const restrictToProductInStockColors =
      isInStockAvailabilityValue(selectedAvailability) &&
      (primaryRoot.hasAttribute('data-vpl2-has-in-stock-colors') || productInStockNames.length > 0);

    const itemMatchesAvailability = (item) => {
      if (!selectedAvailability) return true;

      const itemTitle = item.getAttribute('data-title') || '';
      if (restrictToProductInStockColors) {
        return (
          item.getAttribute('data-in-stock-on-product') === 'true' ||
          matchesInStockTitle(itemTitle, productInStockNames)
        );
      }

      const itemAvailabilities = splitTokens(item.getAttribute('data-availabilities'));
      return (
        !itemAvailabilities.length ||
        itemAvailabilities.some((availability) =>
          matchesAvailability(availability, selectedAvailability)
        )
      );
    };

    // Available Materials considering Availability & Grade for the first (option-dependent) metaobject
    const availableMaterials = new Set();
    swatchItems.forEach((item) => {
      const itemMaterial = item.getAttribute('data-material') || '';
      const itemGrade = item.getAttribute('data-grade') || '';

      if (itemMatchesAvailability(item)) {
        if (!selectedGrade || matchesGrade(itemGrade, selectedGrade)) {
          splitFieldValues(itemMaterial).forEach((material) => {
            availableMaterials.add(normalizeMatchToken(material));
          });
        }
      }
    });

    // Available Grades considering Availability & Material for the first (option-dependent) metaobject
    const availableGrades = new Set();
    swatchItems.forEach((item) => {
      const itemMaterial = item.getAttribute('data-material') || '';
      const itemGrade = item.getAttribute('data-grade') || '';

      if (itemMatchesAvailability(item)) {
        if (!selectedMaterial || matchesMaterial(itemMaterial, selectedMaterial)) {
          splitFieldValues(itemGrade).forEach((grade) => {
            availableGrades.add(normalizeGradeToken(grade));
          });
        }
      }
    });

    // Available Availabilities considering Material & Grade for primary body fabric
    const availableAvailabilities = new Set();
    swatchItems.forEach((item) => {
      const itemMaterial = item.getAttribute('data-material') || '';
      const itemGrade = item.getAttribute('data-grade') || '';
      const itemAvailabilities = splitTokens(item.getAttribute('data-availabilities'));

      if (!selectedMaterial || matchesMaterial(itemMaterial, selectedMaterial)) {
        if (!selectedGrade || matchesGrade(itemGrade, selectedGrade)) {
          itemAvailabilities.forEach((availability) => {
            availableAvailabilities.add(normalizeMatchToken(availability));
          });
        }
      }
    });

    picker.querySelectorAll('input[data-variant-input]').forEach((input) => {
      const optionName = input.getAttribute('name');
      const valToken = normalizeMatchToken(input.value);
      let isAvailable = true;

      if (isMaterialOptionName(optionName)) {
        isAvailable = availableMaterials.has(valToken);
      } else if (isGradeOptionName(optionName)) {
        isAvailable = availableGrades.has(normalizeGradeToken(input.value));
      } else if (isAvailabilityOptionName(optionName)) {
        isAvailable = availableAvailabilities.has(valToken);
      }

      const label = input.nextElementSibling || picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);

      if (!isAvailable) {
        input.classList.add('disabled');
        input.disabled = true;
        input.setAttribute('disabled', 'disabled');
        if (label) {
          label.classList.add('disabled', 'is-disabled');
        }
      } else {
        input.classList.remove('disabled');
        input.disabled = false;
        input.removeAttribute('disabled');
        if (label) {
          label.classList.remove('disabled', 'is-disabled');
        }
      }
    });

    // Auto-select first available option if current selection is disabled
    picker.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      if (row.hasAttribute('data-vpl2-custom-swatch')) return;

      const disabledChecked = row.querySelector('input[data-variant-input]:checked.disabled');
      if (disabledChecked) {
        const firstAvailable = row.querySelector('input[data-variant-input]:not(.disabled)');
        if (firstAvailable) {
          firstAvailable.checked = true;
          syncLabelState(firstAvailable, picker);

          const rowIndex = row.getAttribute('data-vpl2-row');
          const displayLabel = picker.querySelector(`[data-vpl2-sel-display="${rowIndex}"]`);
          if (displayLabel) displayLabel.textContent = firstAvailable.value;

          // Re-trigger filtering only on the Material/Grade-dependent group
          getOptionDependentSwatchRoots(picker).forEach((customRoot) => {
            filterCustomSwatches(customRoot);
          });
        }
      }
    });
  };

  const clearCustomSwatchSelection = (root) => {
    if (!root) return;

    root.querySelectorAll('[data-vpl2-swatch-input]:checked, [data-vpl2-category-input]:checked').forEach((input) => {
      input.checked = false;
      document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
    });

    updateCustomSwatchSummary(root);
  };

  const syncPillowGroupVisibility = (picker) => {
    if (!(picker instanceof Element)) return;

    const showPillow = shouldShowPillowForMaterial(getSelectedMaterial(picker));

    picker.querySelectorAll('[data-vpl2-custom-swatch]').forEach((root) => {
      if (!isPillowGroup(root)) return;

      const shouldHide = !showPillow;
      const isHidden = root.hasAttribute('hidden');

      if (shouldHide === isHidden) {
        if (shouldHide) updateCustomSwatchSummary(root);
        return;
      }

      if (shouldHide) {
        root.setAttribute('hidden', '');
        closeRow(root);
        clearCustomSwatchSelection(root);
        closeDrawer(getDrawerForRoot(root));
        return;
      }

      root.removeAttribute('hidden');
      filterCustomSwatches(root);
    });
  };

  // Theme variant JS updates radio.checked without firing `change`.
  // Scoped to [data-vpl2-picker] only — never hides Availability/Material buttons.
  const syncPickerState = (picker) => {
    if (!(picker instanceof Element) || !picker.hasAttribute('data-vpl2-mto-picker')) return;

    picker.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      // Skip Custom Swatch row here; it has no variant inputs.
      if (row.hasAttribute('data-vpl2-custom-swatch')) return;

      const checked = row.querySelector('input[data-variant-input]:checked');
      if (!checked) return;

      // Hidden Color/Swatch rows still keep inputs for variant matching.
      if (!row.hasAttribute('hidden') && !row.classList.contains('vpl2-hidden-option')) {
        const rowIndex = row.getAttribute('data-vpl2-row');
        const displayLabel = picker.querySelector(`[data-vpl2-sel-display="${rowIndex}"]`);
        if (displayLabel) displayLabel.textContent = checked.value;
        syncLabelState(checked, picker);
      }
    });

    updateAvailabilityShippingMessage(picker);

    // Independent groups skip Material/Grade; the first group maps to those options.
    picker.querySelectorAll('[data-vpl2-custom-swatch]').forEach((customRoot) => {
      filterCustomSwatches(customRoot);
    });

    updateOptionAvailabilityStates(picker);
    syncPillowGroupVisibility(picker);
  };

  const SPEC_LABELS = [
    'Country of Origin',
    'Pilling Resistance',
    'Cleaning Code',
    'Fabric Types',
    'Certifications',
    'Martindale',
    'Protection',
    'Direction',
    'Abrasion',
    'Contains',
    'Content',
    'Material',
    'Color',
    'Grade',
  ];

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const parseLabelValuePairs = (text) => {
    const source = String(text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (!source) return [];

    const labelPattern = new RegExp(
      `\\b(${SPEC_LABELS.map(escapeRegExp).join('|')})\\s*:\\s*`,
      'gi'
    );
    const labels = [];
    let match;

    while ((match = labelPattern.exec(source)) !== null) {
      labels.push({
        label: match[1].trim(),
        valueStart: match.index + match[0].length,
        index: match.index,
      });
    }

    return labels
      .map((item, index) => {
        const valueEnd = index + 1 < labels.length ? labels[index + 1].index : source.length;
        const value = source.slice(item.valueStart, valueEnd).trim();
        if (!item.label || !value) return null;
        return { label: item.label, value };
      })
      .filter(Boolean);
  };

  const parseDescriptionSpecs = (descriptionHtml) => {
    if (!descriptionHtml) return [];

    const lineText = descriptionHtml
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h\d)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\u00a0/g, ' ');

    const lineSpecs = [];
    lineText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const singleLine = line.match(/^([^:]+):\s*(.+)$/);
        if (singleLine && !parseLabelValuePairs(line).slice(1).length) {
          lineSpecs.push({
            label: singleLine[1].trim(),
            value: singleLine[2].trim(),
          });
          return;
        }

        parseLabelValuePairs(line).forEach((spec) => lineSpecs.push(spec));
      });

    if (lineSpecs.length) return lineSpecs;

    return parseLabelValuePairs(lineText);
  };

  const buildDrawerContent = (entry, drawer) => {
    const fabricLabel = drawer.getAttribute('data-label-fabric') || 'Fabric Types';
    const gradeLabel = drawer.getAttribute('data-label-grade') || 'Grade';
    const descriptionLabel = drawer.getAttribute('data-label-description') || 'Description:';

    const fabricValue = entry.content || entry.material || '';
    const gradeMatch = parseDescriptionSpecs(entry.descriptionHtml || '').find((spec) =>
      /^grade$/i.test(spec.label)
    );
    const gradeValue = gradeMatch?.value || '';

    const metaParts = [];
    if (fabricValue) metaParts.push(`<strong>${escapeHtml(fabricLabel)}:</strong> ${escapeHtml(fabricValue)}`);
    if (gradeValue) metaParts.push(`<strong>${escapeHtml(gradeLabel)}:</strong> ${escapeHtml(gradeValue)}`);

    const specs = parseDescriptionSpecs(entry.descriptionHtml || '').filter(
      (spec) => !/^(grade|colou?r)$/i.test(spec.label)
    );

    const imageSrc = entry.imageLarge || entry.image;
    const imageHtml = imageSrc
      ? `<img class="vpl2-drawer__image" src="${escapeHtml(imageSrc)}" alt="${escapeHtml(entry.title)}" width="640" height="640" loading="lazy">`
      : `<div class="vpl2-drawer__image vpl2-drawer__image--fallback" ${entry.color ? `data-vpl2-color="${escapeHtml(entry.color)}"` : ''}></div>`;

    const specsHtml = specs.length
      ? `<ol class="vpl2-drawer__specs">
          ${specs
            .map(
              (spec, index) => `
                <li class="vpl2-drawer__spec">
                  <span class="vpl2-drawer__spec-index">${index + 1}.</span>
                  <span class="vpl2-drawer__spec-copy">
                    <strong>${escapeHtml(spec.label)}:</strong> ${escapeHtml(spec.value)}
                  </span>
                </li>
              `
            )
            .join('')}
        </ol>`
      : entry.descriptionHtml
        ? `<div class="vpl2-drawer__rte rte">${entry.descriptionHtml}</div>`
        : '';

    return `
      <div class="vpl2-drawer__card">
        ${imageHtml}
        <h3 class="vpl2-drawer__name">${escapeHtml(entry.title)}</h3>
        ${metaParts.length ? `<p class="vpl2-drawer__meta">${metaParts.join('<span class="vpl2-drawer__meta-dot">•</span>')}</p>` : ''}
        ${
          specsHtml
            ? `<div class="vpl2-drawer__description">
                <h4 class="vpl2-drawer__description-title">${escapeHtml(descriptionLabel)}</h4>
                ${specsHtml}
              </div>`
            : ''
        }
      </div>
    `;
  };

  const closeDrawer = (drawer = activeDrawer) => {
    if (!drawer || drawer.classList.contains('is-closing')) return;
    if (!drawer.classList.contains('is-open') && drawer.hidden) return;

    const panel = drawer.querySelector('[data-vpl2-drawer-panel]');

    const finishClose = () => {
      drawer.classList.remove('is-active', 'is-closing', 'is-open');
      drawer.hidden = true;
      document.body.classList.remove('vpl2-drawer-open');

      if (activeDrawer === drawer) activeDrawer = null;

      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    };

    drawer.classList.add('is-closing');
    drawer.classList.remove('is-open');

    if (!panel) {
      finishClose();
      return;
    }

    let closed = false;
    const onTransitionEnd = (event) => {
      if (closed) return;
      if (event.target !== panel) return;
      if (event.propertyName && event.propertyName !== 'transform') return;

      closed = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(() => {
      if (closed) return;
      closed = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    }, 400);
  };

  const openDrawer = (root, selectedTitle = '') => {
    const drawer = getDrawerForRoot(root);
    const body = drawer?.querySelector('[data-vpl2-drawer-body]');
    if (!drawer || !body) return;

    const title =
      String(selectedTitle || '').trim() ||
      [...root.querySelectorAll('[data-vpl2-swatch-input]:checked')].find(isVisibleOption)?.value ||
      '';
    const entry = (root._vpl2Entries || []).find((item) => item.title === title);

    // Do not fall back to an unrelated first entry (e.g. other Material).
    if (!entry) return;

    body.innerHTML = buildDrawerContent(entry, drawer);
    body.querySelectorAll('[data-vpl2-color]').forEach((element) => {
      const color = element.getAttribute('data-vpl2-color');
      if (color) element.style.backgroundColor = color;
    });

    if (!lastFocusedElement) {
      lastFocusedElement = document.activeElement;
    }

    drawer.hidden = false;
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-active');
    document.body.classList.add('vpl2-drawer-open');
    activeDrawer = drawer;

    // Force reverse-ready start position, then animate in.
    drawer.classList.remove('is-open');
    void drawer.offsetWidth;

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });

    const closeButton = drawer.querySelector('.vpl2-drawer__close');
    closeButton?.focus();
  };

  const renderCustomSwatch = (root) => {
    const dataNode = root.querySelector('[data-vpl2-swatch-data]');
    const categoryGrid = root.querySelector('[data-vpl2-categories]');
    const swatchGrid = root.querySelector('[data-vpl2-swatch-grid]');
    if (!dataNode || !categoryGrid || !swatchGrid) return;

    let entries = [];
    try {
      entries = JSON.parse(dataNode.textContent || '[]');
    } catch (error) {
      console.error('Custom swatch JSON parse failed', error);
      return;
    }

    if (!Array.isArray(entries) || !entries.length) return;

    const sectionId = root.getAttribute('data-vpl2-section') || 'swatch';
    const filterByProductOptions = shouldFilterByProductOptions(root);
    const pillowGroup = isPillowGroup(root);
    const multiplePillows = allowsMultiplePillows(root);
    const categoryMap = new Map();
    const normalizedEntries = entries
      .map((entry) => {
        const title = String(entry.title || '').trim();
        if (!title) return null;

        let categories = normalizeCategories(entry.categories);
        if (pillowGroup) {
          categories = categories
            .map((category) => resolvePillowCategory(category)?.name)
            .filter(Boolean);
        }
        if (!categories.length) categories = ['All'];

        const material = splitFieldValues(entry.material).join('||');
        const grade = splitFieldValues(entry.grade).join('||');
        const availabilities = normalizeAvailabilityValues(entry.availability);

        categories.forEach((category) => {
          const pillowCategory = pillowGroup ? resolvePillowCategory(category) : null;
          const categoryName = pillowCategory?.name || category;
          const categoryColor = pillowGroup
            ? pillowCategory?.hex || canonicalCategoryColor(categoryName) || ''
            : canonicalCategoryColor(categoryName) || entry.color || '';

          if (!categoryMap.has(categoryName)) {
            categoryMap.set(categoryName, {
              color: categoryColor,
              materials: new Set(),
              availabilities: new Set(),
            });
          }

          const categoryData = categoryMap.get(categoryName);
          if (pillowGroup) {
            if (pillowCategory?.hex) categoryData.color = pillowCategory.hex;
          } else {
            const canonical = canonicalCategoryColor(categoryName);
            if (canonical) categoryData.color = canonical;
            else if (entry.color && !categoryData.color) categoryData.color = entry.color;
          }

          if (material) {
            splitFieldValues(material).forEach((value) => categoryData.materials.add(value));
          }
          availabilities.forEach((availability) => categoryData.availabilities.add(availability));
        });

        return {
          ...entry,
          title,
          material,
          grade,
          availabilities,
          categories,
          inStockOnProduct: Boolean(entry.inStockOnProduct),
        };
      })
      .filter(Boolean);

    root._vpl2Entries = normalizedEntries;

    const categories = [...categoryMap.entries()]
      .filter(([, data]) => Boolean(data.color))
      .map(([name]) => name)
      .sort((a, b) => {
        const aBeige = normalizeToken(a) === 'beige';
        const bBeige = normalizeToken(b) === 'beige';
        return aBeige === bBeige ? 0 : aBeige ? -1 : 1;
      });
    const selectedCategory = categories[0] || '';

    categoryGrid.innerHTML = categories
      .map((category, index) => {
        const inputId = `CustomCategory-${sectionId}-${index}`;
        const categoryData =
          categoryMap.get(category) || { color: '', materials: new Set(), availabilities: new Set() };
        const color = categoryData.color || '';
        const materials = [...categoryData.materials].join('||');
        const availabilities = [...categoryData.availabilities].join('||');
        const checked = category === selectedCategory ? 'checked' : '';
        const activeClass = category === selectedCategory ? ' is-active' : '';
        const circleStyle = color ? `background-color: ${escapeHtml(color)};` : '';

        return `
          <li
            class="vpl2-swatch-item"
            data-vpl2-category-item
            data-category="${escapeHtml(category)}"
            data-materials="${escapeHtml(materials)}"
            data-availabilities="${escapeHtml(availabilities)}"
            ${color ? `data-vpl2-color="${escapeHtml(color)}"` : ''}
          >
            <input
              type="radio"
              class="vpl2-input"
              name="custom-category-${escapeHtml(sectionId)}"
              id="${escapeHtml(inputId)}"
              value="${escapeHtml(category)}"
              data-vpl2-category-input
              ${checked}
            >
            <label class="vpl2-swatch-card${activeClass}" for="${escapeHtml(inputId)}">
              <span class="vpl2-swatch-circle vpl2-category-dot" ${color ? `data-vpl2-color="${escapeHtml(color)}"` : ''} ${circleStyle ? `style="${circleStyle}"` : ''}></span>
              <span class="vpl2-swatch-label">${escapeHtml(category)}</span>
            </label>
          </li>
        `;
      })
      .join('');

    const infoLabel = escapeHtml(root.getAttribute('data-label-swatch-info') || 'View material details');

    swatchGrid.innerHTML = normalizedEntries
      .map((entry, index) => {
        const categoryTokens = entry.categories.join('||');
        const availabilityTokens = entry.availabilities.join('||');
        const inputId = `CustomSwatch-${sectionId}-${entry.id || index}`;
        const media = entry.image
          ? `<img class="vpl2-fabric-image" src="${escapeHtml(entry.image)}" alt="${escapeHtml(entry.title)}" width="120" height="120" loading="lazy">`
          : `<span class="vpl2-fabric-fallback" ${entry.color ? `data-vpl2-color="${escapeHtml(entry.color)}"` : ''}></span>`;
        const itemMaterial = filterByProductOptions ? entry.material || '' : '';
        const itemGrade = filterByProductOptions ? entry.grade || '' : '';
        const inputType = multiplePillows ? 'checkbox' : 'radio';

        return `
          <li
            class="vpl2-fabric-item"
            data-vpl2-swatch-item
            data-material="${escapeHtml(itemMaterial)}"
            data-grade="${escapeHtml(itemGrade)}"
            data-availabilities="${escapeHtml(availabilityTokens)}"
            data-categories="${escapeHtml(categoryTokens)}"
            data-title="${escapeHtml(entry.title)}"
            data-in-stock-on-product="${entry.inStockOnProduct ? 'true' : 'false'}"
          >
            <input
              type="${inputType}"
              class="vpl2-input"
              name="custom-swatch-${escapeHtml(sectionId)}"
              id="${escapeHtml(inputId)}"
              value="${escapeHtml(entry.title)}"
              data-vpl2-swatch-input
              data-category="${escapeHtml(entry.categories[0] || '')}"
            >
            <label class="vpl2-fabric-card" for="${escapeHtml(inputId)}">
              <span class="vpl2-fabric-media">${media}</span>
              <span class="vpl2-fabric-label">${escapeHtml(entry.title)}</span>
            </label>
            <button
              type="button"
              class="vpl2-swatch-info"
              data-vpl2-swatch-info
              aria-label="${infoLabel}"
              title="${infoLabel}"
            >${SWATCH_INFO_ICON}</button>
          </li>
        `;
      })
      .join('');

    root.querySelectorAll('[data-vpl2-color]').forEach((element) => {
      const color = element.getAttribute('data-vpl2-color');
      if (color) element.style.backgroundColor = color;
    });

    filterCustomSwatches(root);
  };

  const initCustomSwatch = (root) => {
    if (!root || root.dataset.vpl2Ready === 'true') return;
    root.dataset.vpl2Ready = 'true';
    renderCustomSwatch(root);
  };

  document.addEventListener(
    'mousedown',
    (event) => {
      const picker = getMtoPicker(event.target);
      if (!picker) return;

      const toggle = event.target.closest('[data-vpl2-toggle]');
      if (toggle && picker.contains(toggle)) {
        event.preventDefault();
        return;
      }

      if (event.target.closest('[data-vpl2-swatch-info]')) return;

      const label = event.target.closest('label[for]');
      if (!label || !picker.contains(label)) return;

      const input = document.getElementById(label.htmlFor);
      if (!input?.classList.contains('vpl2-input')) return;
      event.preventDefault();
    },
    true
  );

  document.addEventListener('click', (event) => {
    const pillowLabel = event.target.closest('label[for]');
    if (pillowLabel) {
      const pillowRoot = getCustomSwatchRoot(pillowLabel);
      if (allowsMultiplePillows(pillowRoot)) {
        const pillowInput = document.getElementById(pillowLabel.htmlFor);
        if (
          pillowInput?.matches('[data-vpl2-swatch-input]') &&
          !pillowInput.checked &&
          getCheckedSwatchInputs(pillowRoot).length >= getMaxMultiplePillows(pillowRoot)
        ) {
          event.preventDefault();
          return;
        }
      }
    }

    if (event.target.closest('[data-vpl2-drawer-close]')) {
      const drawer = event.target.closest('[data-vpl2-drawer]');
      if (drawer && getMtoPicker(drawer)) closeDrawer(drawer);
      return;
    }

    const infoButton = event.target.closest('[data-vpl2-swatch-info]');
    if (infoButton) {
      event.preventDefault();
      const root = getCustomSwatchRoot(infoButton);
      const item = infoButton.closest('[data-vpl2-swatch-item]');
      const title = item?.getAttribute('data-title') || '';
      if (root && title) openDrawer(root, title);
      return;
    }

    const toggle = event.target.closest('[data-vpl2-toggle]');
    if (!toggle) return;

    const picker = getMtoPicker(toggle);
    if (!picker) return;

    const selectedRow = toggle.closest('[data-vpl2-row]');
    if (!selectedRow) return;

    event.preventDefault();
    toggle.focus({ preventScroll: true });

    const shouldOpen = !selectedRow.classList.contains('is-open');

    preserveScrollPosition(() => {
      if (shouldOpen) {
        openRow(selectedRow);

        if (isCustomSwatchTriggerRow(selectedRow)) {
          openCustomSwatchRows(picker);
        } else if (shouldFilterByProductOptions(selectedRow)) {
          openMaterialRows(picker);
        }
        return;
      }

      closeRow(selectedRow);
      if (selectedRow.hasAttribute('data-vpl2-custom-swatch')) {
        closeDrawer(getDrawerForRoot(selectedRow));
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) {
      closeDrawer(activeDrawer);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target;

    if (input.matches('[data-vpl2-mto-picker] input[data-variant-input]')) {
      if (!input.checked) return;

      const picker = getMtoPicker(input);
      const row = input.closest('[data-vpl2-row]');
      if (!picker || !row) return;

      const rowIndex = row.getAttribute('data-vpl2-row');
      const displayLabel = picker.querySelector(`[data-vpl2-sel-display="${rowIndex}"]`);
      if (displayLabel) displayLabel.textContent = input.value;

      row.querySelectorAll('.vpl2-btn-label, .vpl2-swatch-card').forEach((label) => {
        label.classList.remove('is-active');
      });
      document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.add('is-active');

      // Only the first metaobject group maps to Material/Grade product options.
      const optionName = input.getAttribute('name');
      if (isMaterialOptionName(optionName) || isGradeOptionName(optionName) || isAvailabilityOptionName(optionName)) {
        if (isAvailabilityOptionName(optionName)) {
          updateAvailabilityShippingMessage(picker);
        }

        getOptionDependentSwatchRoots(picker).forEach((customRoot) => {
          filterCustomSwatches(customRoot);

          const drawer = getDrawerForRoot(customRoot);
          if (
            drawer &&
            (drawer.classList.contains('is-open') || drawer.classList.contains('is-active'))
          ) {
            closeDrawer(drawer);
          }
        });

        preserveScrollPosition(() => {
          syncPillowGroupVisibility(picker);
          openCustomSwatchRows(picker);
          openRow(row);
        });
      }
      return;
    }

    const customRoot = getCustomSwatchRoot(input);
    if (!customRoot) return;

    if (input.matches('[data-vpl2-category-input]')) {
      syncLabelState(input);
      filterCustomSwatches(customRoot);
      return;
    }

    if (input.matches('[data-vpl2-swatch-input]')) {
      if (allowsMultiplePillows(customRoot) && input.checked) {
        const max = getMaxMultiplePillows(customRoot);
        if (getCheckedSwatchInputs(customRoot).length > max) {
          input.checked = false;
        }
      }

      syncLabelState(input);
      enforceMultiplePillowLimit(customRoot);
      updateCustomSwatchSummary(customRoot);
      if (getSelectedCustomSwatchValues(customRoot).swatch) {
        clearRequiredSwatchError(customRoot);
      }
    }
  });

  document.addEventListener('click', (event) => {
    const closeBtn = event.target.closest('[data-mto-required-close]');
    if (!closeBtn) return;

    const picker = getMtoPicker(closeBtn);
    if (!picker) return;

    event.preventDefault();
    closeRequiredOptionsDialog(picker);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openDialog = document.querySelector('[data-vpl2-mto-picker] [data-mto-required-dialog].is-open');
    if (!openDialog) return;

    const picker = getMtoPicker(openDialog);
    if (!picker) return;

    event.preventDefault();
    closeRequiredOptionsDialog(picker);
  });

  // Keep line-item properties in sync right before Add to Cart builds FormData.
  // Block add-to-cart when a visible MTO swatch group (e.g. Pillow) has no selection.
  // No-op on forms without an MTO Custom Swatch block.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const roots = getCustomSwatchRootsForForm(form);
      if (!roots.length) return;

      if (!validateRequiredMtoSwatches(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-add-to-cart], button.add-to-cart, button[name="add"]');
      if (!button || button.disabled) return;
      if (!document.querySelector('[data-vpl2-mto-picker]')) return;

      const form = button.form || button.closest('form');
      if (!form) return;

      const roots = getCustomSwatchRootsForForm(form);
      if (!roots.length) return;

      if (!validateRequiredMtoSwatches(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // Inject selected Custom Swatch values into the cart payload FormData.
  // Scoped to product forms that include [data-vpl2-custom-swatch].
  document.addEventListener('formdata', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    syncCustomSwatchPropertiesToFormData(event.target, event.formData);
  });

  let refreshQueued = false;
  const refreshMtoPickers = () => {
    const pickers = document.querySelectorAll('[data-vpl2-mto-picker]');
    if (!pickers.length) return;

    if (refreshQueued) return;
    refreshQueued = true;

    window.requestAnimationFrame(() => {
      refreshQueued = false;
      document.querySelectorAll('[data-vpl2-mto-picker]').forEach((picker) => {
        syncPickerState(picker);
      });
    });
  };

  document.addEventListener('variant:change', refreshMtoPickers);

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    refreshMtoPickers();
  });

  const boot = () => {
    document.querySelectorAll('[data-vpl2-mto-picker]').forEach((picker) => {
      const roots = [...picker.querySelectorAll('[data-vpl2-custom-swatch]')];
      if (roots.length && !roots.some((root) => shouldFilterByProductOptions(root))) {
        roots[0].setAttribute('data-vpl2-filter-by-product-options', '');
      }

      roots.forEach((root) => {
        if (shouldFilterByProductOptions(root)) return;
        root.setAttribute('data-vpl2-independent-swatch', '');
      });
    });

    document.querySelectorAll('[data-vpl2-mto-picker] [data-vpl2-custom-swatch]').forEach(initCustomSwatch);
    refreshMtoPickers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
